// HeaderAppBar.tsx
import React, { useCallback, useMemo } from "react";
import {
    Box,
    useScrollTrigger,
    useMediaQuery,
    Grid,
    useTheme,
    Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Hamburger from "hamburger-react";

import ButtonLink from "../buttons/ButtonLink";
import LanguageSelect from "../selects/LanguageSelect";
import SwitchAppBar from "../switches/SwitchAppBar";
import { DrawerItem, menuItems } from "../drawer/ItemsDrawer";

interface HeaderAppBarProps {
    open: boolean;
    setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    setLevel: (value: number) => void;
    setHistorico: React.Dispatch<React.SetStateAction<DrawerItem[] | []>>;
    handleDrawerState: any;
}

/** A11y: “skip link” para pular o menu e ir direto ao conteúdo */
const skipLinkSx = {
    position: "absolute",
    left: 8,
    top: -60,
    zIndex: 2000,
    px: 1.5,
    py: 1,
    borderRadius: 1,
    backgroundColor: "rgba(0,0,0,0.9)",
    color: "white",
    textDecoration: "none",
    fontSize: 14,
    "&:focus": {
        top: 8,
        outline: "2px solid rgba(255,255,255,0.9)",
        outlineOffset: 2,
    },
} as const;

const HeaderAppBar: React.FC<HeaderAppBarProps> = ({
    open,
    setOpenDrawer,
    setLevel,
    setHistorico,
    handleDrawerState,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobileS = useMediaQuery("(max-width: 320px)");
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const isNotBigEnough = useMediaQuery("(max-width: 980px)");

    const fitSustentabilidade = useMediaQuery("(min-width: 1200px)");
    const trigger = useScrollTrigger();

    const { t } = useTranslation();

    const appBarHeight = { xs: "42px", sm: "48px", md: "48px", lg: "48px", xl: "50px" };

    // ids para A11y do menu/drawer
    const drawerNavId = "site-drawer-nav";
    const primaryNavId = "site-primary-nav";

    const resetDrawerState = useCallback(() => {
        setLevel(0);
        setOpenDrawer(false);
        setHistorico([]);
    }, [setHistorico, setOpenDrawer, setLevel]);

    const handleButtonClick = useCallback(
        (level: string) => {
            if (level === "SOCIAL") {
                navigate("/projetosocial");
                resetDrawerState();
            } else if (level === "SUSTENTABILIDADE") {
                navigate("/sustentabilidade");
                resetDrawerState();
            } else {
                setOpenDrawer(true);
                setLevel(1);
                const item = menuItems.find((item) => item.label === level);
                setHistorico(item ? [item] : []);
            }
        },
        [navigate, resetDrawerState, setHistorico, setOpenDrawer, setLevel]
    );

    const isActivePath = useCallback(
        (path: string) => {
            // ajuste fino se você tiver rotas com parâmetros
            return location.pathname === path;
        },
        [location.pathname]
    );

    const coreLogo = useMemo(() => {
        return (
            <MuiLink
                component={RouterLink}
                to="/"
                aria-label={t("Appbar.goHome")}
                title={t("Appbar.goHome")}
                underline="none"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    lineHeight: 0,
                    ml: isMobileS ? 0 : 1,
                }}
            >
                <Box
                    component="img"
                    src="/assets/CoreLogo.avif"
                    alt={t("Appbar.logoAlt")}
                    sx={{
                        height: appBarHeight,
                        width: isMobileS ? "175px" : "auto",
                        cursor: "pointer",
                        display: "block",
                    }}
                />
            </MuiLink>
        );
    }, [appBarHeight, isMobileS, t]);

    return (
        <Grid
            container
            component="header"
            role="banner"
            sx={{
                position: "fixed",
                top: trigger ? "-100px" : "0",
                transition: "top 0.3s ease-in-out",
                left: 0,
                width: "100%",
                height: appBarHeight,
                minHeight: appBarHeight,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                zIndex: 1500,
                display: "flex",
                alignItems: "center",
            }}
        >
            {/* Skip link (fica visível ao focar com TAB) */}
            <Box
                component="a"
                href="#main-content"
                sx={skipLinkSx}
            >
                {t("Appbar.skipToContent")}
            </Box>

            <Grid
                size={{ xs: 2, sm: 10, md: !isNotBigEnough ? 4.6 : 10.9, lg: 4, xl: 4 }}
                sx={{
                    display: "flex",
                    justifyContent: isMobile || isTablet || isNotBigEnough ? "left" : "right",
                    zIndex: (th) => th.zIndex.drawer + 1,
                    height: appBarHeight,
                    alignItems: "center",
                }}
            >
                <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
                    {/* Hamburguer: estado acessível + ligação com o drawer */}
                    <Box
                        role="button"
                        aria-label={open ? t("Appbar.closeMenu") : t("Appbar.openMenu")}
                        aria-controls={drawerNavId}
                        aria-expanded={open ? "true" : "false"}
                        // hamburger-react já lida com clique, mas mantemos o wrapper semântico
                        sx={{ display: "inline-flex", alignItems: "center" }}
                    >
                        <Hamburger
                            label={open ? t("Appbar.closeMenu") : t("Appbar.openMenu")}
                            duration={0.7}
                            size={25}
                            color="white"
                            toggled={open}
                            onToggle={() => handleDrawerState()}
                        />
                    </Box>

                    {!isMobile && coreLogo}

                    {/* Mantém seu comportamento atual, mas sem prejudicar A11y */}
                    {isMobile && open ? (
                        <Box aria-label={t("Appbar.idioma")}>
                            <LanguageSelect />
                        </Box>
                    ) : (
                        <Box aria-label={t("Appbar.toggleMode")}>
                            <SwitchAppBar />
                        </Box>
                    )}
                </Box>
            </Grid>

            {/* Nav primário (desktop) */}
            <Grid
                size={{ xs: 4, sm: 6, md: 6.5, lg: 7, xl: 6 }}
                component="nav"
                aria-label={t("Appbar.primaryNavLabel")}
                id={primaryNavId}
                sx={{
                    display: isTablet || isMobile || isNotBigEnough ? "none" : "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: appBarHeight,
                    gap: 1,
                    textAlign: "center",
                }}
            >
                <ButtonLink
                    text={t("Appbar.loja")}
                    href="https://core-esportes-aquaticos.lojaintegrada.com.br/"
                    external
                    ariaLabel={t("Appbar.buttonLojaAriaLabel")}
                    title={t("Appbar.buttonLojaTitle")}
                />

                <ButtonLink
                    text={t("KITE")}
                    handleClickDrawer={() => handleButtonClick("KITE")}
                    ariaLabel={t("Appbar.buttonKiteAriaLabel")}
                />

                <ButtonLink
                    text={t("WING")}
                    handleClickDrawer={() => handleButtonClick("WING")}
                    ariaLabel={t("Appbar.buttonWingAriaLabel")}
                />

                <ButtonLink
                    text={t("Appbar.comunidade")}
                    handleClickDrawer={() => handleButtonClick("COMUNIDADE")}
                    ariaLabel={t("Appbar.buttonComunidadeAriaLabel")}
                />

                <ButtonLink
                    text={t("SOCIAL")}
                    handleClickDrawer={() => handleButtonClick("SOCIAL")}
                    ariaLabel={t("Appbar.buttonSocialAriaLabel")}
                    title={t("Appbar.buttonSocialTitle")}
                    ariaCurrent={isActivePath("/projetosocial") ? "page" : undefined}
                />

                {fitSustentabilidade ? (
                    <ButtonLink
                        text={t("HomePage.sustentabilidade")}
                        handleClickDrawer={() => handleButtonClick("SUSTENTABILIDADE")}
                        ariaLabel={t("Appbar.buttonSustentabilidadeAriaLabel")}
                        ariaCurrent={isActivePath("/sustentabilidade") ? "page" : undefined}
                    />
                ) : null}

            </Grid>

            {/* Seletor de idioma (desktop) */}
            <Grid
                size={{ xs: 2, sm: 2, md: 0.5, lg: 1, xl: 2 }}
                sx={{
                    height: appBarHeight,
                    flexDirection: "row",
                    alignItems: "center",
                }}
                display={isMobile ? "none" : "flex"}
            >
                <Box aria-label={t("Selecionar idioma")}>
                    <LanguageSelect />
                </Box>
            </Grid>

            {/* Logo no mobile à direita */}
            <Grid
                size={{ xs: 10 }}
                sx={{
                    display: !isMobile ? "none" : "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    height: appBarHeight,
                }}
            >
                {coreLogo}
            </Grid>
        </Grid>
    );
};

export default HeaderAppBar;
